import * as Dialog from '@radix-ui/react-dialog';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { logger } from '@/lib/logger';

import { TextSkeleton } from '@/components/Skeleton';

import { buttons } from '@/styles/buttons';
import { dialogCloseBtnStyles } from '@/styles/dialog';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import { CopyIcon } from '@/ui/Icons/CopyIcon';
import { FarcasterIcon } from '@/ui/Icons/FarcasterIcon';
import { ShareIcon } from '@/ui/Icons/ShareIcon';
import { TwitterIcon } from '@/ui/Icons/TwitterIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';

import { getBaseURL } from '@/utils/environment';
import { ChainAddress } from '@/utils/models';
import { tokenRouteHref, tradeRoute } from '@/utils/url';

import { AnimatedCard } from '../../AnimatedCard';

interface TokenSharingModalProps {
  chainId: number;
  address: ChainAddress;
  symbol: string;
  openTime: number;
  isOpen: boolean;
  handleOpenChange: (open: boolean) => void;
}

export const TokenSharingModal = ({
  chainId,
  address,
  symbol,
  openTime,
  isOpen,
  handleOpenChange,
}: TokenSharingModalProps) => {
  const [hasClickedCopyButton, setHasClickedCopyButton] = useState(false);
  const [isDesktopOgImageLoading, setIsDesktopOgImageLoading] = useState(true);
  const [isMobileOgImageLoading, setIsMobileOgImageLoading] = useState(true);
  const [isTabPressed, setIsTabPressed] = useState(false);

  const isMobile = useMedia(breakpoints.mobile, false);
  const ogImageUrl = `${getBaseURL()}/api/token-og?chainId=${chainId}&address=${address}&time=${openTime}`;
  const PAGE_DESCRIPTION = `Checkout $${symbol} on @matchaxyz`;

  const getSharePageURL = (chainId: number, address: ChainAddress) =>
    `${getBaseURL()}${tokenRouteHref(tradeRoute({ chainId, address }))}`;

  const onShareOnX = () => {
    const encodedShareText = encodeURIComponent(PAGE_DESCRIPTION);
    const encodedSharePageURL = encodeURIComponent(getSharePageURL(chainId, address));
    window.open(
      `https://x.com/intent/tweet?text=${encodedShareText}%0A%0A${encodedSharePageURL}`,
      '_blank',
    );
  };

  const onShareOnFarcaster = () => {
    const encodedShareText = encodeURIComponent(PAGE_DESCRIPTION);
    const encodedSharePageURL = encodeURIComponent(getSharePageURL(chainId, address));
    window.open(
      `https://farcaster.xyz/~/compose?text=${encodedShareText}&embeds[]=${encodedSharePageURL}`,
      '_blank',
    );
  };

  const onCopyLink = () => {
    navigator.clipboard.writeText(getSharePageURL(chainId, address));
  };

  const onCopyLinkClick = () => {
    onCopyLink();
    setHasClickedCopyButton(true);
    setTimeout(() => setHasClickedCopyButton(false), 2000);
  };

  const onShareClick = () => {
    try {
      navigator.share({
        text: `${PAGE_DESCRIPTION}\n`,
        url: getSharePageURL(chainId, address),
      });
    } catch (error) {
      logger.error('Error sharing:', error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setIsTabPressed(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      {isMobile ? (
        <BottomPanelModal
          isOpen={isOpen}
          onClose={() => {
            handleOpenChange(false);
            setIsMobileOgImageLoading(true);
          }}
          onOpenChange={(value) => {
            handleOpenChange(value);
            if (!value) setIsMobileOgImageLoading(true);
          }}
          variant="LARGE"
        >
          <div className="flex flex-col gap-3 pt-6 pb-6 px-4 z-10">
            <Dialog.Title className={fontStyles({ variant: 'H5', class: ['pb-1'] })}>
              Share
            </Dialog.Title>
            {isMobileOgImageLoading && (
              <TextSkeleton
                width="100%"
                height={208}
                className="-mb-[11px]"
                lineClassName="rounded-[14px]!"
              />
            )}

            <Image
              src={ogImageUrl}
              width="358"
              height="192"
              alt="ogImage"
              onLoad={() => {
                setIsMobileOgImageLoading(false);
              }}
              style={{
                width: 'auto',
                marginBottom: '8px',
                height: isMobileOgImageLoading ? '0' : 'auto',
                borderRadius: '14px',
              }}
            />

            <button
              className={buttons({
                shape: 'pill',
                color: 'gray',
                size: 'small',
                class: ['w-full h-9'],
              })}
              onClick={onShareOnX}
            >
              <TwitterIcon className="mr-2" /> Share on X.com
            </button>

            <button
              className={buttons({
                shape: 'pill',
                color: 'gray',
                size: 'small',
                class: ['w-full h-9'],
              })}
              onClick={onShareOnFarcaster}
            >
              <FarcasterIcon className="mr-2" /> Share on Farcaster
            </button>

            <button
              className={buttons({
                shape: 'pill',
                color: 'gray',
                size: 'small',
                class: ['w-full h-9'],
              })}
              onClick={onShareClick}
            >
              <ShareIcon className="mr-2" /> Share
            </button>
          </div>
        </BottomPanelModal>
      ) : (
        <Modal
          isOpen={isOpen}
          onClose={() => {
            handleOpenChange(false);
            setIsDesktopOgImageLoading(true);
            setIsTabPressed(false);
          }}
          onOpenChange={(value) => {
            handleOpenChange(value);
            if (!value) {
              setIsDesktopOgImageLoading(true);
              setIsTabPressed(false);
            }
          }}
          variant="DEFAULT"
        >
          <div className="py-5 px-6 pb-6">
            <h5 className={fontStyles({ variant: 'H5', class: ['mb-5'] })}>Share</h5>
            <Dialog.Close
              className={dialogCloseBtnStyles().button({
                absolute: true,
                class: [!isTabPressed ? 'focus:outline-none' : ''],
              })}
            >
              <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
            </Dialog.Close>
            {isDesktopOgImageLoading && (
              <TextSkeleton width={434} height={228} lineClassName="rounded-[16px]!" />
            )}

            <AnimatedCard
              src={ogImageUrl}
              width={434}
              height={228}
              alt="ogImage"
              onLoad={() => {
                setIsDesktopOgImageLoading(false);
              }}
              isLoading={isDesktopOgImageLoading}
            />

            <div className="flex justify-between mt-6">
              <button
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'gray',
                  class: ['w-[136px]'],
                })}
                onClick={onShareOnX}
              >
                <TwitterIcon className="mr-2" /> X.com
              </button>

              <button
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'gray',
                  class: ['w-[136px]'],
                })}
                onClick={onShareOnFarcaster}
              >
                <FarcasterIcon className="mr-2" /> Farcaster
              </button>

              <button
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'gray',
                  class: ['w-[136px]'],
                })}
                onClick={onCopyLinkClick}
              >
                {hasClickedCopyButton ? (
                  <div className="flex items-center w-[86px]">
                    <CheckCircleIcon className="mr-2" /> Copied
                  </div>
                ) : (
                  <>
                    <CopyIcon className="mr-2" /> Copy link
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
